import React, { useEffect, useState } from 'react';
import { Box, TextField, CircularProgress, Typography, Chip, Grid, Tab, Tabs } from '@mui/material';
import { ThemeProvider } from '@emotion/react';
import MUIDataTable from 'mui-datatables';
import PhoneIcon from '@mui/icons-material/Phone';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import theme from '../../theme';
import api from '../../services/api';

const phoneTypeIcon = (tipo) => {
  switch ((tipo || '').toLowerCase()) {
    case 'celular': return <SmartphoneIcon fontSize="small" />;
    case 'whatsapp': return <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />;
    default: return <PhoneIcon fontSize="small" />;
  }
};

const tableOpts = {
  filterType: 'checkbox',
  rowsPerPageOptions: [10, 30, 50],
  rowsPerPage: 10,
  rowHover: false,
  print: false,
  selectableRows: 'none',
  download: false,
  elevation: 0,
};

export default function Empresa({ codEmpresa }) {
  const [info, setInfo] = useState(null);
  const [contatos, setContatos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (!codEmpresa) return;
    setLoading(true);
    Promise.all([
      api.get(`/empresa-athena/info/${codEmpresa}`),
      api.get(`/empresa-athena/contatos/${codEmpresa}`),
    ])
      .then(([infoRes, contatosRes]) => {
        setInfo(infoRes.data);
        setContatos(contatosRes.data || []);
      })
      .catch(() => { setInfo(null); setContatos([]); })
      .finally(() => setLoading(false));
  }, [codEmpresa]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress /></Box>;
  if (!info) return <Typography sx={{ mt: 2 }}>Nenhum dado encontrado.</Typography>;

  const { company, links = [], servicos = [], asns = [], blocos_ip = [] } = info;

  const fields = [
    { label: 'Código da Empresa', value: company.codigo_empresa },
    { label: 'Razão Social', value: company.name },
    { label: 'Nome Fantasia', value: company.fantasy_name },
    { label: 'CNPJ', value: company.cnpj },
    { label: 'Email', value: company.email },
  ];

  const contatoCols = [
    { name: 'nome', label: 'Nome', options: { filter: true, sort: true } },
    { name: 'cargo', label: 'Cargo', options: { filter: true, sort: true } },
    { name: 'email', label: 'Email', options: { filter: true, sort: true } },
    { name: 'tipo_contato_nome', label: 'Tipo', options: { filter: true, sort: true } },
    {
      name: 'telefones', label: 'Telefones', options: {
        filter: false, sort: false,
        customBodyRender: (value) => {
          if (!value || value.length === 0) return '-';
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {value.map((t, i) => (
                <Chip key={i} icon={phoneTypeIcon(t.tipo)} label={t.telefone} size="small" variant="outlined" />
              ))}
            </Box>
          );
        }
      }
    },
  ];

  const linkCols = [
    { name: 'tipo_link_nome', label: 'Tipo', options: { filter: true, sort: true } },
    { name: 'operadora_nome', label: 'Operadora', options: { filter: true, sort: true } },
    { name: 'numero_circuito', label: 'Circuito', options: { filter: true, sort: true } },
    { name: 'observacoes', label: 'Observações', options: { filter: false, sort: true } },
  ];

  const asnCols = [
    {
      name: 'asn_number', label: 'ASN', options: {
        filter: true, sort: true,
        customBodyRender: (value) => `AS${value}`,
      }
    },
    { name: 'descricao', label: 'Descrição', options: { filter: true, sort: true } },
  ];

  const blocoCols = [
    {
      name: 'tipo', label: 'Tipo', options: {
        filter: true, sort: true,
        customBodyRender: (value) => (
          <Chip label={(value || '').toUpperCase()} size="small" color={value === 'ipv6' ? 'secondary' : 'primary'} variant="outlined" />
        ),
      }
    },
    { name: 'bloco_cidr', label: 'Bloco CIDR', options: { filter: true, sort: true } },
    { name: 'gateway', label: 'Gateway', options: { filter: true, sort: true } },
    { name: 'observacoes', label: 'Observações', options: { filter: false, sort: true } },
  ];

  const servicoCols = [
    { name: 'servico_descricao', label: 'Serviço', options: { filter: true, sort: true } },
    { name: 'observacoes', label: 'Observações', options: { filter: false, sort: true } },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: '100%' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="Empresa" />
          <Tab label={`Contatos (${contatos.length})`} />
          <Tab label={`Links (${links.length})`} />
          <Tab label={`ASNs (${asns.length})`} />
          <Tab label={`Blocos IP (${blocos_ip.length})`} />
          <Tab label={`Serviços (${servicos.length})`} />
        </Tabs>

        {tabIndex === 0 && (
          <Box>
            <Grid container spacing={1}>
              {fields.map((f, i) => (
                <Grid item xs={12} md={i === 1 ? 12 : 6} key={i}>
                  <TextField
                    size="small"
                    margin="dense"
                    label={f.label}
                    value={f.value || ''}
                    fullWidth
                    variant="outlined"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {tabIndex === 1 && (
          <MUIDataTable data={contatos} columns={contatoCols} options={{ ...tableOpts, textLabels: { body: { noMatch: 'Nenhum contato cadastrado' } } }} />
        )}

        {tabIndex === 2 && (
          <MUIDataTable data={links} columns={linkCols} options={{ ...tableOpts, textLabels: { body: { noMatch: 'Nenhum link cadastrado' } } }} />
        )}

        {tabIndex === 3 && (
          <MUIDataTable data={asns} columns={asnCols} options={{ ...tableOpts, textLabels: { body: { noMatch: 'Nenhum ASN cadastrado' } } }} />
        )}

        {tabIndex === 4 && (
          <MUIDataTable data={blocos_ip} columns={blocoCols} options={{ ...tableOpts, textLabels: { body: { noMatch: 'Nenhum bloco IP cadastrado' } } }} />
        )}

        {tabIndex === 5 && (
          <MUIDataTable data={servicos} columns={servicoCols} options={{ ...tableOpts, rowsPerPage: 30, textLabels: { body: { noMatch: 'Nenhum serviço contratado' } } }} />
        )}
      </Box>
    </ThemeProvider>
  );
}
